import {compact} from '@benchling/js-utils';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {
  ProcessSchemaMapperType,
  ProcessMapperTokenType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {LookupConfigSpec, LookupStepSpec, LookupStepType} from 'lookup-config/LookupConfigModalConstants';
import {
  ProcessFlowchartSchemaFieldMapperConfigSpec,
  ProcessFlowchartSchemaMapperConfigSpec,
  ProcessFlowchartSchemaMapperSpec,
} from 'processes/dao/ProcessFlowchartConfigDAO';
import {
  ProcessFlowchartEdgeType,
  TransitionSource,
  FlowchartStaticEdgeData,
  CustomMappingData,
  TaskSchemaNodeSectionType,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';

import {
  getSchemaFieldClientIdFromHandleAndNode,
  getSchemaFieldInfoFromHandle,
} from './flowchartIdentifierUtils';
import {TaskSchemaStaticNodeData} from './processFlowchartConfigEditorNodeTypes';
import {getDefaultTransitionSource, getTransitionSourceByNodeIdPairKey} from './TransitionSourceUtils';

interface BuildSchemaMapperConfigsFromStaticDataRunnerArgs {
  nodes: Array<TaskSchemaStaticNodeData>;
  edges: Array<FlowchartStaticEdgeData>;
  customMappingDataByEdgeId: Record<string, CustomMappingData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
}

interface BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  schemaMapperConfigSpecs: Array<ProcessFlowchartSchemaMapperConfigSpec>;
  inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;
}

class BuildSchemaMapperConfigsFromStaticDataRunner {
  private readonly nodes: Array<TaskSchemaStaticNodeData>;
  private readonly edges: Array<FlowchartStaticEdgeData>;
  private readonly customMappingDataByEdgeId: Record<string, CustomMappingData>;
  private readonly transitionSourceByNodeIdPair: Record<string, TransitionSource>;

  private inboundSchemaMapperConfigsByNodeId: Record<string, Array<ProcessFlowchartSchemaMapperSpec>>;

  constructor(args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs) {
    this.nodes = args.nodes;
    this.edges = args.edges;
    this.customMappingDataByEdgeId = args.customMappingDataByEdgeId;
    this.transitionSourceByNodeIdPair = args.transitionSourceByNodeIdPair;

    this.inboundSchemaMapperConfigsByNodeId = {};
  }

  run(): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
    // Find all control flow edges to iterate over them instead of nodes
    const controlFlowEdges = this.edges.filter((edge) => edge.type === ProcessFlowchartEdgeType.CONTROL_FLOW);

    // SchemaMapperConfigSpecs are passed directly to the backend
    const schemaMapperConfigSpecs = compact(
      controlFlowEdges.map((controlFlowEdge) =>
        this.buildSchemaMapperConfigForControlFlowEdge(controlFlowEdge)
      )
    );
    return {
      schemaMapperConfigSpecs,
      inboundSchemaMapperConfigsByNodeId: this.inboundSchemaMapperConfigsByNodeId,
    };
  }

  /**
   * This currently assumes all transitions are non-merging, and will create an inbound schema mapper config
   * for each control flow edge going to a target node.
   */
  buildSchemaMapperConfigForControlFlowEdge(
    controlFlowEdge: FlowchartStaticEdgeData
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    // Find the target node for this control flow edge
    const targetNode = this.nodes.find((node) => node.id === controlFlowEdge.target);
    assert(targetNode != null, 'Expected to find target node', {edge: controlFlowEdge});

    const clientId = uuidv4();
    const schemaFieldMapperConfigsAndSchemas = this.buildSchemaFieldMapperConfigsForControlFlowEdge(
      controlFlowEdge,
      targetNode
    );

    if (schemaFieldMapperConfigsAndSchemas.length === 0) {
      assert(false, 'Expected to find schema field mapper configs for the control flow edge', {
        controlFlowEdge,
        targetNode,
      });
    }

    if (this.inboundSchemaMapperConfigsByNodeId[targetNode.id] == null) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id] = [];
    }
    this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
      toNodeConfigClientId: targetNode.id,
      schemaMapperConfigClientId: clientId,
      schemaMapperDependencies: Array.from(
        new Map(
          schemaFieldMapperConfigsAndSchemas.map(({fromNodeConfigClientId}) => [
            fromNodeConfigClientId,
            {fromNodeConfigClientId},
          ])
        ).values()
      ),
    });

    const mapsToType =
      targetNode.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
        ? ProcessMapperTokenType.PROCESS_OUTPUT
        : ProcessMapperTokenType.PROCESS_TASK;

    return {
      clientId,
      toProcessTaskDefinitionVersionClientId: targetNode.taskDefinitionVersionSpec.clientId,
      mapsToType,
      schemaFieldMapperConfigs: schemaFieldMapperConfigsAndSchemas.map(
        ({fieldMapperConfig}) => fieldMapperConfig
      ),
      mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
    };
  }

  buildSchemaMapperConfig(
    targetNode: TaskSchemaStaticNodeData
  ): ProcessFlowchartSchemaMapperConfigSpec | null {
    const clientId = uuidv4();
    const schemaFieldMapperConfigsAndSchemas = this.buildSchemaFieldMapperConfigsForNode(targetNode);

    if (schemaFieldMapperConfigsAndSchemas.length === 0) {
      return null;
    }

    if (this.inboundSchemaMapperConfigsByNodeId[targetNode.id] == null) {
      this.inboundSchemaMapperConfigsByNodeId[targetNode.id] = [];
    }
    this.inboundSchemaMapperConfigsByNodeId[targetNode.id].push({
      toNodeConfigClientId: targetNode.id,
      schemaMapperConfigClientId: clientId,
      schemaMapperDependencies: Array.from(
        new Map(
          schemaFieldMapperConfigsAndSchemas.map(({fromNodeConfigClientId}) => [
            fromNodeConfigClientId,
            {fromNodeConfigClientId},
          ])
        ).values()
      ),
    });

    const mapsToType =
      targetNode.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
        ? ProcessMapperTokenType.PROCESS_OUTPUT
        : ProcessMapperTokenType.PROCESS_TASK;

    return {
      clientId,
      toProcessTaskDefinitionVersionClientId: targetNode.taskDefinitionVersionSpec.clientId,
      mapsToType,
      schemaFieldMapperConfigs: schemaFieldMapperConfigsAndSchemas.map(
        ({fieldMapperConfig}) => fieldMapperConfig
      ),
      mapperType: ProcessSchemaMapperType.FIELD_MAPPER,
    };
  }

  private getTransitionSourceForNode(
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): TransitionSource {
    const transitionSource =
      this.transitionSourceByNodeIdPair[getTransitionSourceByNodeIdPairKey(sourceNode.id, targetNode.id)] ??
      getDefaultTransitionSource(sourceNode.id, this.nodes);
    return transitionSource;
  }

  private buildSchemaFieldMapperConfigsForNode(node: TaskSchemaStaticNodeData): Array<{
    fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec;
    fromTaskDefinitionVersionId: string;
    fromNodeConfigClientId: string;
  }> {
    const dataFlowEdges = this.edges.filter(
      (edge) => edge.type === ProcessFlowchartEdgeType.DATA_FLOW && edge.target === node.id
    );
    return dataFlowEdges.map((edge) => this.buildFieldMapperConfigFromDataFlowEdge(edge, node));
  }

  private buildSchemaFieldMapperConfigsForControlFlowEdge(
    controlFlowEdge: FlowchartStaticEdgeData,
    targetNode: TaskSchemaStaticNodeData
  ): Array<{
    fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec;
    fromTaskDefinitionVersionId: string;
    fromNodeConfigClientId: string;
  }> {
    // Find data flow edges that match the same source and target as this control flow edge
    const matchingDataFlowEdges = this.edges.filter(
      (edge) =>
        edge.type === ProcessFlowchartEdgeType.DATA_FLOW &&
        edge.source === controlFlowEdge.source &&
        edge.target === controlFlowEdge.target
    );
    return matchingDataFlowEdges.map((edge) => this.buildFieldMapperConfigFromDataFlowEdge(edge, targetNode));
  }

  buildFieldMapperConfigFromDataFlowEdge(
    dataFlowEdge: FlowchartStaticEdgeData,
    targetNode: TaskSchemaStaticNodeData
  ): {
    fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec;
    fromTaskDefinitionVersionId: string;
    fromNodeConfigClientId: string;
  } {
    const sourceNode = this.nodes.find((node) => node.id === dataFlowEdge.source);
    assert(sourceNode != null, 'Expected to find source node', {edge: dataFlowEdge});
    const fromTaskDefinitionVersionId = sourceNode.taskDefinitionVersionSpec.clientId;

    const fromFieldHandle = dataFlowEdge.sourceHandle;
    const toFieldHandle = dataFlowEdge.targetHandle;
    assert(fromFieldHandle != null, `fromFieldHandle is null for edge ${dataFlowEdge.id}`);
    assert(toFieldHandle != null, `toFieldHandle is null for edge ${dataFlowEdge.id}`);
    const fromFieldInfo = getSchemaFieldInfoFromHandle(fromFieldHandle);

    const toFieldClientId = getSchemaFieldClientIdFromHandleAndNode(toFieldHandle, targetNode);
    const customMappingData = this.customMappingDataByEdgeId[dataFlowEdge.id];

    const transitionSource = this.getTransitionSourceForNode(sourceNode, targetNode);
    const mapsFromType =
      transitionSource === TransitionSource.OUTPUTS
        ? ProcessMapperTokenType.PROCESS_OUTPUT
        : ProcessMapperTokenType.PROCESS_TASK;

    const fieldMapperConfig: ProcessFlowchartSchemaFieldMapperConfigSpec = {
      fromProcessTaskDefinitionVersionClientId: fromTaskDefinitionVersionId,
      mapsFromType,
      toProcessFieldClientIdentifier: toFieldClientId,
      resultMustBeNonNullToMap: customMappingData?.resultMustBeNonNullToMap ?? false,
      lookupConfig:
        customMappingData?.lookupConfig != null
          ? customMappingData.lookupConfig
          : this.getLookupConfigFromFieldAndTransitionSource(fromFieldInfo, sourceNode, targetNode),
    };

    return {
      fieldMapperConfig,
      fromTaskDefinitionVersionId,
      fromNodeConfigClientId: sourceNode.id,
    };
  }

  private getLookupConfigFromFieldAndTransitionSource(
    fieldInfo: {
      fieldId: string;
      sectionName: string;
    },
    sourceNode: TaskSchemaStaticNodeData,
    targetNode: TaskSchemaStaticNodeData
  ): LookupConfigSpec {
    const lookupSteps: Array<LookupStepSpec> = [];
    // If the transition source is the node output but the field is coming from the node input, we need to add
    // a lookup step to go from the output to the inputs (aka from the process output to the process task).
    if (
      fieldInfo.sectionName === TaskSchemaNodeSectionType.INPUTS &&
      this.getTransitionSourceForNode(sourceNode, targetNode) === TransitionSource.OUTPUTS
    ) {
      lookupSteps.push({type: LookupStepType.PROCESS_TASK});
    }
    lookupSteps.push({type: LookupStepType.SCHEMA_FIELD, schemaField: fieldInfo.fieldId});
    return {
      isMulti: false,
      lookupSteps,
    };
  }
}

/**
 * Builds the default field mapper config for a data flow edge.
 *
 * This is used to differentiate between a "field-to-field" mapping configured by drawing an edge between two
 * fields and a "custom mapping" configured by a custom lookup config.
 */
export function buildDefaultSchemaFieldMapperConfigFromDataFlowEdge({
  dataFlowEdge,
  nodes,
  transitionSourceByNodeIdPair,
}: {
  dataFlowEdge: FlowchartStaticEdgeData;
  nodes: Array<TaskSchemaStaticNodeData>;
  transitionSourceByNodeIdPair: Record<string, TransitionSource>;
}): ProcessFlowchartSchemaFieldMapperConfigSpec {
  const targetNode = nodes.find((node) => node.id === dataFlowEdge.target);
  assert(targetNode != null, 'Expected to find target node', {edge: dataFlowEdge});

  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner({
    nodes,
    edges: [dataFlowEdge],
    customMappingDataByEdgeId: {},
    transitionSourceByNodeIdPair,
  });
  return runner.buildFieldMapperConfigFromDataFlowEdge(dataFlowEdge, targetNode).fieldMapperConfig;
}

export default function buildSchemaMapperConfigsFromStaticData(
  args: BuildSchemaMapperConfigsFromStaticDataRunnerArgs
): BuildSchemaMapperConfigsFromStaticDataRunnerResult {
  const runner = new BuildSchemaMapperConfigsFromStaticDataRunner(args);
  return runner.run();
}
